import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

const API_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => api(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null, '');
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        window.location.href = '/admin/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
  register: (data: { email: string; password: string; name: string }) =>
    api.post('/auth/register', data),
};

// Projects API
export const projectsApi = {
  getAll: (params?: { page?: number; limit?: number; category?: string; featured?: boolean }) =>
    api.get('/projects', { params }),
  getBySlug: (slug: string) => api.get(`/projects/${slug}`),
  create: (data: Record<string, unknown>) => api.post('/projects', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/projects/${id}`, data),
  delete: (id: string) => api.delete(`/projects/${id}`),
  reorder: (orders: Array<{ id: string; order: number }>) =>
    api.post('/projects/reorder', { orders }),
};

// Pages API
export const pagesApi = {
  getAll: () => api.get('/pages'),
  getBySlug: (slug: string) => api.get(`/pages/${slug}`),
  create: (data: Record<string, unknown>) => api.post('/pages', data),
  update: (id: string, data: Record<string, unknown>) => api.put(`/pages/${id}`, data),
  delete: (id: string) => api.delete(`/pages/${id}`),
};

// Media API
export const mediaApi = {
  getAll: (params?: { page?: number; limit?: number; type?: string }) =>
    api.get('/media', { params }),
  upload: (file: File, alt?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (alt) formData.append('alt', alt);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadMultiple: (files: File[]) => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return api.post('/media/upload-multiple', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  update: (id: string, alt: string) => api.put(`/media/${id}`, { alt }),
  delete: (id: string) => api.delete(`/media/${id}`),
};

// Contact API
export const contactApi = {
  submit: (data: {
    name: string;
    email: string;
    company?: string;
    subject?: string;
    message: string;
  }) => api.post('/contact/submit', data),
  getAll: (params?: { page?: number; limit?: number; unread?: boolean }) =>
    api.get('/contact', { params }),
  getById: (id: string) => api.get(`/contact/${id}`),
  markRead: (id: string) => api.put(`/contact/${id}/read`),
  markUnread: (id: string) => api.put(`/contact/${id}/unread`),
  delete: (id: string) => api.delete(`/contact/${id}`),
};

// Settings API
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data: Record<string, unknown>) => api.put('/settings', data),
};

// Users API
export const usersApi = {
  getAll: () => api.get('/users'),
  getById: (id: string) => api.get(`/users/${id}`),
  update: (id: string, data: Record<string, unknown>) => api.put(`/users/${id}`, data),
  changePassword: (id: string, currentPassword: string, newPassword: string) =>
    api.put(`/users/${id}/password`, { currentPassword, newPassword }),
  delete: (id: string) => api.delete(`/users/${id}`),
};

// AI Profiles API (AISTRYX)
export const aiProfilesApi = {
  getAll: () => api.get('/ai-profiles'),
  getByType: (type: string) => api.get(`/ai-profiles/${type}`),
  upsert: (data: { name: string; type: string; content: string; active?: boolean }) =>
    api.post('/ai-profiles', data),
  update: (type: string, data: Record<string, unknown>) => api.put(`/ai-profiles/${type}`, data),
  getLogs: (type: string, params?: { page?: number; limit?: number }) =>
    api.get(`/ai-profiles/${type}/logs`, { params }),
};

export default api;
